import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  linkWithPopup,
  signInWithCredential,
  signInAnonymously,
  signOut as firebaseSignOut
} from "firebase/auth";
import {
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  setDoc
} from "firebase/firestore/lite";

import { normalizeAchievementProgress } from "./achievements.js";

export const FIREBASE_CONFIG = Object.freeze({
  apiKey: "AIzaSyCRKIdjoVcf5oHqnqRXAvAUONXvClQIaWQ",
  authDomain: "idiot-5b462.firebaseapp.com",
  projectId: "idiot-5b462",
  storageBucket: "idiot-5b462.firebasestorage.app",
  messagingSenderId: "1040528721949",
  appId: "1:1040528721949:web:df5f282c8d646e0469def4",
  measurementId: "G-ESJ3CJEJC0"
});

export const PLAYER_BACKUP_SCHEMA_VERSION = 2;
export const GOOGLE_PROVIDER_ID = GoogleAuthProvider.PROVIDER_ID;
const SETTINGS_STORAGE_KEY = "idiot2.settings.v1";
const STATS_STORAGE_KEY = "idiot2.stats.v1";
const ACHIEVEMENT_STORAGE_KEY = "idiot2.achievements.v1";

export function getFirebaseProjectDetails(config = FIREBASE_CONFIG) {
  return {
    projectId: config.projectId ?? "",
    authDomain: config.authDomain ?? "",
    appId: config.appId ?? "",
    storageBucket: config.storageBucket ?? "",
    messagingSenderId: config.messagingSenderId ?? ""
  };
}

export function getPlayerDocumentPath(uid) {
  return uid ? `players/${uid}` : "players/{uid}";
}

export function getGoogleProviderProfile(user) {
  return getUserProviderData(user).find((provider) => provider.providerId === GOOGLE_PROVIDER_ID) ?? null;
}

export function isGoogleLinkedUser(user) {
  return Boolean(getGoogleProviderProfile(user));
}

export function normalizeBackupStatistics(statistics = {}, streakStats = {}) {
  const normal = normalizeResultCounts(statistics.normal);
  const hard = normalizeResultCounts(statistics.hard);
  const revenge = normalizeResultCounts(statistics.revenge);
  return {
    normal,
    hard,
    revenge,
    totalGames: normal.wins + normal.losses + hard.wins + hard.losses,
    totalRevengeGames: revenge.wins + revenge.losses,
    streaks: {
      daily: normalizeStreakCounts(streakStats.daily),
      win: normalizeStreakCounts(streakStats.win)
    }
  };
}

export function normalizeBackupAchievements(progress) {
  const normalized = normalizeAchievementProgress(progress);
  return Object.entries(normalized.unlocked).map(([id, record]) => ({
    id,
    earnedAt: normalizeBackupDate(record.unlockedAt)
  }));
}

export function normalizePlayerBackupDocument(data = {}) {
  const localStorage = normalizeStoredLocalStorage(data?.localStorage);
  const localSettings = parseLocalStorageJson(localStorage[SETTINGS_STORAGE_KEY]);
  const localStats = parseLocalStorageJson(localStorage[STATS_STORAGE_KEY]);
  const localAchievements = parseLocalStorageJson(localStorage[ACHIEVEMENT_STORAGE_KEY]);
  const achievementProgress = localAchievements ? normalizeAchievementProgress(localAchievements) : null;
  const statistics = data?.statistics && typeof data.statistics === "object" ? data.statistics : {};
  return {
    name: getStoredBackupName(data, localSettings),
    uid: typeof data?.uid === "string" ? data.uid : "",
    schemaVersion: normalizeCount(data?.schemaVersion) || PLAYER_BACKUP_SCHEMA_VERSION,
    lastBackupAt: normalizeStoredBackupDate(data?.lastBackupAt ?? data?.updatedAt ?? data?.createdAt),
    localStorage,
    statistics: localStats
      ? normalizeBackupStatistics(localStats, getStreakStatsFromAchievementProgress(achievementProgress))
      : normalizeBackupStatistics(statistics, statistics.streaks),
    achievements: achievementProgress
      ? normalizeBackupAchievements(achievementProgress)
      : normalizeStoredBackupAchievements(data?.achievements)
  };
}

export function createPlayerBackupPayload({
  uid,
  name,
  localStorage,
  statistics,
  streakStats,
  achievements,
  documentExists = false,
  existingCreatedAt = null,
  now = new Date(),
  createServerTimestamp = serverTimestamp
} = {}) {
  const normalizedUid = typeof uid === "string" ? uid : "";
  if (!normalizedUid) {
    throw new Error("Cannot create player backup without an authenticated user id.");
  }

  const payload = {
    name: typeof name === "string" && name.trim() ? name.trim() : "You"
  };

  Object.assign(payload, {
    createdAt: documentExists && existingCreatedAt ? existingCreatedAt : createServerTimestamp(),
    updatedAt: createServerTimestamp(),
    lastBackupAt: normalizeBackupDate(now),
    uid: normalizedUid,
    schemaVersion: PLAYER_BACKUP_SCHEMA_VERSION,
    localStorage: normalizeStoredLocalStorage(localStorage),
    statistics: normalizeBackupStatistics(statistics, streakStats),
    achievements: normalizeBackupAchievements(achievements)
  });

  return payload;
}

export async function getAnonymousFirebaseUser(auth, dependencies = {}) {
  if (!auth) {
    throw new Error("Firebase Auth is not available.");
  }

  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  if (auth.currentUser) {
    return auth.currentUser;
  }

  const signIn = dependencies.signInAnonymously ?? signInAnonymously;
  const credential = await signIn(auth);
  if (!credential?.user) {
    throw new Error("Anonymous sign-in did not return a Firebase user.");
  }
  return credential.user;
}

export async function getAnonymousPlayerIdentity(dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  return createPlayerIdentityResult(user, config);
}

export function getFirebaseApp(config = FIREBASE_CONFIG) {
  return getApps().length > 0 ? getApp() : initializeApp(config);
}

export async function readPlayerBackupMetadata(dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const firestore = dependencies.firestore ?? getFirestore(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  const backup = await readPlayerBackupForUser(user, firestore, dependencies);

  return {
    uid: user.uid,
    documentPath: backup.documentPath,
    exists: backup.exists,
    createdAt: backup.createdAt,
    updatedAt: backup.updatedAt,
    lastBackupAt: backup.lastBackupAt,
    backup,
    projectDetails: getFirebaseProjectDetails(config),
    ...createLinkedProviderResult(user)
  };
}

export async function linkAnonymousPlayerToGoogle(dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  const previousUid = user.uid;

  if (isGoogleLinkedUser(user)) {
    return createGoogleLinkResult(user, previousUid, "already-linked", config);
  }

  try {
    const link = dependencies.linkWithPopup ?? linkWithPopup;
    const provider = createGoogleAuthProvider(dependencies);
    const credential = await link(user, provider);
    const linkedUser = credential?.user ?? auth.currentUser ?? user;
    if (previousUid && linkedUser?.uid && linkedUser.uid !== previousUid) {
      throw new Error("Google sign-in changed the player ID. Your anonymous player ID and local progress are unchanged.");
    }
    return createGoogleLinkResult(linkedUser, previousUid, "linked", config);
  } catch (error) {
    if (getFirebaseErrorCode(error) === "auth/provider-already-linked") {
      return createGoogleLinkResult(auth.currentUser ?? user, previousUid, "already-linked", config);
    }
    if (isExistingGoogleAccountError(error)) {
      const firestore = dependencies.firestore ?? getFirestore(app);
      return signInExistingGoogleAccount({
        auth,
        firestore,
        error,
        previousUid,
        config,
        dependencies
      });
    }
    throw createGoogleLinkError(error);
  }
}

export async function signOutGooglePlayer(dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  const previousUid = user.uid ?? null;

  if (!isGoogleLinkedUser(user)) {
    return {
      ...createPlayerIdentityResult(user, config),
      previousUid,
      signedOut: false,
      status: "not-linked",
      lastBackupAt: null
    };
  }

  const signOut = dependencies.signOut ?? firebaseSignOut;
  await signOut(auth);
  const anonymousUser = await getAnonymousFirebaseUser(auth, dependencies);
  return {
    ...createPlayerIdentityResult(anonymousUser, config),
    previousUid,
    signedOut: true,
    status: "signed-out",
    lastBackupAt: null
  };
}

export async function backupPlayerData({
  name,
  localStorage,
  statistics,
  streakStats,
  achievements,
  now = new Date()
} = {}, dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const firestore = dependencies.firestore ?? getFirestore(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  const documentPath = getPlayerDocumentPath(user.uid);
  const documentRef = dependencies.doc ? dependencies.doc(firestore, "players", user.uid) : doc(firestore, "players", user.uid);
  const snapshot = await (dependencies.getDoc ?? getDoc)(documentRef);
  const documentExists = Boolean(snapshot?.exists?.());
  const existingData = documentExists && typeof snapshot?.data === "function" ? snapshot.data() : {};
  const payload = createPlayerBackupPayload({
    uid: user.uid,
    name,
    localStorage,
    statistics,
    streakStats,
    achievements,
    documentExists,
    existingCreatedAt: existingData?.createdAt ?? null,
    now,
    createServerTimestamp: dependencies.serverTimestamp ?? serverTimestamp
  });

  await (dependencies.setDoc ?? setDoc)(documentRef, payload);

  return {
    uid: user.uid,
    documentPath,
    lastBackupAt: payload.lastBackupAt,
    projectDetails: getFirebaseProjectDetails(config),
    ...createLinkedProviderResult(user),
    payload
  };
}

export async function deletePlayerBackupData(dependencies = {}) {
  const config = dependencies.config ?? FIREBASE_CONFIG;
  const app = dependencies.app ?? getFirebaseApp(config);
  const auth = dependencies.auth ?? getAuth(app);
  const firestore = dependencies.firestore ?? getFirestore(app);
  const user = await getAnonymousFirebaseUser(auth, dependencies);
  const documentPath = getPlayerDocumentPath(user.uid);
  const documentRef = dependencies.doc ? dependencies.doc(firestore, "players", user.uid) : doc(firestore, "players", user.uid);

  await (dependencies.deleteDoc ?? deleteDoc)(documentRef);

  return {
    uid: user.uid,
    documentPath,
    lastBackupAt: null,
    projectDetails: getFirebaseProjectDetails(config),
    ...createLinkedProviderResult(user)
  };
}

function createPlayerIdentityResult(user, config = FIREBASE_CONFIG) {
  return {
    uid: user.uid,
    documentPath: getPlayerDocumentPath(user.uid),
    projectDetails: getFirebaseProjectDetails(config),
    ...createLinkedProviderResult(user)
  };
}

function createLinkedProviderResult(user) {
  const googleProfile = getGoogleProviderProfile(user);
  return {
    isAnonymous: Boolean(user?.isAnonymous) && !googleProfile,
    providerIds: getUserProviderData(user).map((provider) => provider.providerId),
    googleLinked: Boolean(googleProfile),
    googleEmail: googleProfile?.email ?? user?.email ?? null,
    googleDisplayName: googleProfile?.displayName ?? user?.displayName ?? null,
    googlePhotoURL: googleProfile?.photoURL ?? user?.photoURL ?? null
  };
}

function createGoogleLinkResult(user, previousUid, status, config = FIREBASE_CONFIG) {
  return {
    ...createPlayerIdentityResult(user, config),
    previousUid,
    uidPreserved: Boolean(previousUid && user?.uid === previousUid),
    status
  };
}

function createGoogleAuthProvider(dependencies = {}) {
  const provider = dependencies.googleProvider ?? new GoogleAuthProvider();
  if (typeof provider.setCustomParameters === "function") {
    provider.setCustomParameters({ prompt: "select_account" });
  }
  return provider;
}

async function signInExistingGoogleAccount({
  auth,
  firestore,
  error,
  previousUid,
  config,
  dependencies
}) {
  const credential = getGoogleCredentialFromError(error, dependencies);
  if (!credential) {
    throw createGoogleLinkError(error);
  }

  const signIn = dependencies.signInWithCredential ?? signInWithCredential;
  const credentialResult = await signIn(auth, credential);
  const signedInUser = credentialResult?.user ?? auth.currentUser;
  if (!signedInUser?.uid) {
    throw new Error("Google sign-in did not return a Firebase user.");
  }

  const backup = dependencies.readExistingBackup === false
    ? null
    : await readPlayerBackupForUser(signedInUser, firestore, dependencies);
  return {
    ...createGoogleLinkResult(signedInUser, previousUid, "signed-in-existing", config),
    backup
  };
}

async function readPlayerBackupForUser(user, firestore, dependencies = {}) {
  const documentPath = getPlayerDocumentPath(user?.uid);
  if (!user?.uid) {
    throw new Error("Cannot read player backup without an authenticated user id.");
  }

  const documentRef = dependencies.doc ? dependencies.doc(firestore, "players", user.uid) : doc(firestore, "players", user.uid);
  const snapshot = await (dependencies.getDoc ?? getDoc)(documentRef);
  const exists = Boolean(snapshot?.exists?.());
  const rawData = exists && typeof snapshot?.data === "function" ? snapshot.data() : {};
  const data = exists ? normalizePlayerBackupDocument(rawData) : null;

  return {
    documentPath,
    exists,
    createdAt: normalizeStoredBackupDate(rawData?.createdAt),
    updatedAt: normalizeStoredBackupDate(rawData?.updatedAt),
    lastBackupAt: normalizeStoredBackupDate(rawData?.lastBackupAt ?? rawData?.updatedAt ?? rawData?.createdAt),
    data
  };
}

function getGoogleCredentialFromError(error, dependencies = {}) {
  if (typeof dependencies.credentialFromError === "function") {
    return dependencies.credentialFromError(error);
  }
  if (typeof GoogleAuthProvider.credentialFromError === "function") {
    return GoogleAuthProvider.credentialFromError(error);
  }
  return null;
}

function isExistingGoogleAccountError(error) {
  const code = getFirebaseErrorCode(error);
  return code === "auth/credential-already-in-use" || code === "auth/email-already-in-use";
}

function createGoogleLinkError(error) {
  const code = getFirebaseErrorCode(error);
  if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") {
    return createErrorWithCode(
      "That Google account is already linked to another Idiot backup. Your anonymous player ID and local progress are unchanged.",
      code
    );
  }
  if (code === "auth/popup-closed-by-user") {
    return createErrorWithCode("Google sign-in was closed before it finished.", code);
  }
  if (code === "auth/popup-blocked") {
    return createErrorWithCode("The browser blocked the Google sign-in window. Allow popups for this site and try again.", code);
  }
  return error instanceof Error ? error : new Error("Unable to connect Google sign-in.");
}

function createErrorWithCode(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function getFirebaseErrorCode(error) {
  return typeof error?.code === "string" ? error.code : "";
}

function getUserProviderData(user) {
  return Array.isArray(user?.providerData) ? user.providerData.filter(Boolean) : [];
}

function getStoredBackupName(data, localSettings) {
  if (typeof localSettings?.humanName === "string" && localSettings.humanName.trim()) {
    return localSettings.humanName.trim();
  }
  return typeof data?.name === "string" && data.name.trim() ? data.name.trim() : "You";
}

function getStreakStatsFromAchievementProgress(progress) {
  return {
    daily: {
      current: progress?.aggregate?.dailyStreak ?? 0,
      best: progress?.aggregate?.bestDailyStreak ?? 0
    },
    win: {
      current: progress?.aggregate?.winStreak ?? 0,
      best: progress?.aggregate?.bestWinStreak ?? 0
    }
  };
}

function normalizeStoredLocalStorage(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized = {};
  for (const [key, storedValue] of Object.entries(value)) {
    if (typeof key === "string" && typeof storedValue === "string") {
      normalized[key] = storedValue;
    }
  }
  return normalized;
}

function parseLocalStorageJson(value) {
  if (typeof value !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeStoredBackupAchievements(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((record) => {
      const id = typeof record?.id === "string" ? record.id : "";
      if (!id) {
        return null;
      }
      return {
        id,
        earnedAt: normalizeStoredBackupDate(record.earnedAt ?? record.unlockedAt) ?? new Date(0).toISOString()
      };
    })
    .filter(Boolean);
}

function normalizeResultCounts(value = {}) {
  return {
    wins: normalizeCount(value.wins),
    losses: normalizeCount(value.losses)
  };
}

function normalizeStreakCounts(value = {}) {
  return {
    current: normalizeCount(value.current),
    best: normalizeCount(value.best)
  };
}

function normalizeCount(value) {
  return Math.max(0, Number.parseInt(value ?? 0, 10) || 0);
}

function normalizeBackupDate(value) {
  return normalizeStoredBackupDate(value) ?? new Date().toISOString();
}

function normalizeStoredBackupDate(value) {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === "function") {
    return normalizeStoredBackupDate(value.toDate());
  }

  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    const milliseconds = (value.seconds * 1000) + Math.floor((value.nanoseconds ?? 0) / 1000000);
    return normalizeStoredBackupDate(new Date(milliseconds));
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
